import {
  PREMIUM_CABIN_DESIGNATOR,
  SEAT_CODE_AISLE,
  SEAT_CODE_BASSINET,
  SEAT_CODE_LEGROOM,
  SEAT_CODE_WINDOW
} from '@constants/app.constants';
import { SeatAvailability, SeatMapAvailabiliyWithRows, UnitInformation } from '@models/booking.models';
import { cloneDeep } from 'lodash-es';
import { stringAsBooleanProp } from './data-utils';
import {N0AvailableJourneyFare} from "@models/responses/availability-response.model";
import {CabinProductClass} from "@constants/cabin.constants";
import { N0SsrByJourney } from '@models/responses/ssr-availability-response.model';

/**
 * This function is used to check if a value is between a min and max value.
 */
const isValueBetween = (value: number, min?: number, max?: number) => {
  if (!min && !max) {
    return false;
  }

  if (!min && max) {
    return value < max;
  }

  if (min && !max) {
    return value > min;
  }

  return min && max && value >= min && value <= max;
};

/**
 * This function is used to get the percentage of seats that should be reserved
 * based on the number of units in a compartmentDesignator.
 */
const getPercentByRange = (
  compartmentDesignatorUnitsLength: number,
  firstRange: {
    min?: number;
    max?: number;
    percent: number;
  },
  secondRange?: {
    min?: number;
    max?: number;
    percent: number;
  },
  thirdRange?: {
    min?: number;
    max?: number;
    percent: number;
  },
) => {
  if (
    isValueBetween(
      compartmentDesignatorUnitsLength,
      firstRange.min,
      firstRange.max,
    )
  ) {
    return firstRange.percent;
  } else if (
    secondRange &&
    isValueBetween(
      compartmentDesignatorUnitsLength,
      secondRange?.min,
      secondRange?.max,
    )
  ) {
    return secondRange.percent;
  } else if (
    thirdRange &&
    isValueBetween(
      compartmentDesignatorUnitsLength,
      thirdRange?.min,
      thirdRange?.max,
    )
  ) {
    return thirdRange.percent;
  }

  return 0;
};

/**
 * This function is used to get a random set of reserved seats.
 */
const getRandomReservedSeats = (
  numberOfReservedSeats: number,
  units: UnitInformation[],
) => {
  const reservedUnits: UnitInformation[] = [];

  for (let i = 0; i < numberOfReservedSeats; i++) {
    const randomIndex = Math.floor(Math.random() * (units.length - 2));

    const randomUnit = units[randomIndex];
    const nextUnit = units[randomIndex + 1];
    const nextNextUnit = units[randomIndex + 2];

    if (randomUnit) {
      reservedUnits.push(randomUnit);
    }

    if (nextUnit) {
      reservedUnits.push(nextUnit);
      numberOfReservedSeats--;
    }

    if (nextNextUnit) {
      reservedUnits.push(nextNextUnit);
      numberOfReservedSeats--;
    }
  }

  return reservedUnits;
};

/**
 * Generates a random set of reserved seats for a given set of units.
 * The number of reserved seats is calculated based on the percentage
 * of economy and premium seats that should be reserved.
 */
export const generateRandomReservedSeats = ({
  units,
  economyPercent,
  premiumPercent,
}: {
  units: UnitInformation[];
  economyPercent: {
    firstRange: {
      min?: number;
      max?: number;
      percent: number;
    };
    secondRange?: {
      min?: number;
      max?: number;
      percent: number;
    };
    thirdRange?: {
      min?: number;
      max?: number;
      percent: number;
    };
  };
  premiumPercent: {
    firstRange: {
      min?: number;
      max?: number;
      percent: number;
    };
    secondRange?: {
      min?: number;
      max?: number;
      percent: number;
    };
    thirdRange?: {
      min?: number;
      max?: number;
      percent: number;
    };
  };
}) => {
  const copyOfUnits = cloneDeep(units);

  // filter out bassinet seats as they should not be reserved
  const avalibityUnits = copyOfUnits.filter(
    (unit) =>
      unit.availability === SeatAvailability.Open &&
      !unit.properties?.find(
        (prop) =>
          prop.code === SEAT_CODE_BASSINET && stringAsBooleanProp(prop.value),
      ),
  );

  // calculate the number of seats to reserve for each compartmentDesignator
  const compartmentDesignatorYUnits = avalibityUnits.filter(
    (unit) => unit.compartmentDesignator === 'Y',
  );
  const compartmentDesignatorYUnitsLength = compartmentDesignatorYUnits.length;

  const economyPercentValue = getPercentByRange(
    compartmentDesignatorYUnitsLength,
    economyPercent.firstRange,
    economyPercent.secondRange,
    economyPercent.thirdRange,
  );

  const numberOfReservedSeatsY = Math.floor(
    compartmentDesignatorYUnitsLength * economyPercentValue,
  );

  const compartmentDesignatorWUnits = avalibityUnits.filter(
    (unit) => unit.compartmentDesignator === 'W',
  );

  const premiumPercentValue = getPercentByRange(
    compartmentDesignatorWUnits.length,
    premiumPercent.firstRange,
    premiumPercent.secondRange,
    premiumPercent.thirdRange,
  );

  const numberOfReservedSeatsW = Math.floor(
    compartmentDesignatorWUnits.length * premiumPercentValue,
  );

  const reservedUnitsY = getRandomReservedSeats(
    numberOfReservedSeatsY,
    compartmentDesignatorYUnits,
  );

  const reservedUnitsW = getRandomReservedSeats(
    numberOfReservedSeatsW,
    compartmentDesignatorWUnits,
  );

  // set the availability of the reserved seats to RESERVED
  [...reservedUnitsY, ...reservedUnitsW].forEach((unit: UnitInformation) => {
    if (unit) {
      unit.availability = SeatAvailability.Reserved;
    }
  });

  return copyOfUnits;
};

export const findGivenSeat = (
  units: UnitInformation[],
  isWindowSeat: boolean,
  isAisleSeat: boolean,
  isLegRoomSeat: boolean
): UnitInformation | undefined => {
  return units.find(unit => {

    const windowSeat = unit.properties.find(property => property.code === SEAT_CODE_WINDOW)?.value === 'True';
    const aisleSeat = unit.properties.find(property => property.code === SEAT_CODE_AISLE)?.value === 'True';
    const extraRoomSeat = unit.properties.find(property => property.code === SEAT_CODE_LEGROOM)?.value === 'True';
    const available = unit.availability === SeatAvailability.Open;

    if (windowSeat === isWindowSeat && aisleSeat === isAisleSeat && extraRoomSeat === isLegRoomSeat && available) {
      return unit;
    }
    return undefined;
  });
}

export const findGoodSeat = (units: UnitInformation[], cabinClass: string): UnitInformation | undefined => {
  // For one passenger
  // Window seat (then aisle) as much on the front as possible
  // Extra Legroom for Premium only

  let goodSeat: UnitInformation | undefined;

  const windowSeat = findGivenSeat(units, true, false, cabinClass === PREMIUM_CABIN_DESIGNATOR)

  if (!windowSeat) {
    const aisleSeat = findGivenSeat(units, false, true, cabinClass === PREMIUM_CABIN_DESIGNATOR);

    if (!aisleSeat) {
      goodSeat = findGivenSeat(units, false, false, cabinClass === PREMIUM_CABIN_DESIGNATOR);
    } else {
      goodSeat = aisleSeat;
    }

  } else {
    goodSeat = windowSeat;
  }
  return goodSeat;
}

export const findGoodSeatsForXPassengers = (units: UnitInformation[], cabinClass: string, count: number): (UnitInformation | undefined)[] => {
  // For more than one passenger
  // Seat together in one row as much on the front as possible
  // If no row matches the condition just the first X seats on the front
  // Extra Legroom for Premium only

  const rows = new Map<string, UnitInformation[]>();
  const isPremium = cabinClass === PREMIUM_CABIN_DESIGNATOR;

  units.forEach(unit => {
    const rowNumber = unit.designator.slice(0, -1);
    const extraRoomSeat = unit.properties.some(
      property => property.code === SEAT_CODE_LEGROOM && property.value === 'True'
    );


    rows.set(rowNumber, rows.get(rowNumber) ?? []);

    if (unit.availability === SeatAvailability.Open &&
      (isPremium || (!isPremium && !extraRoomSeat))) {
      rows.get(rowNumber)?.push(unit);
    }
  });

  const consecutiveSeats = findFirstXConsecutiveSeats(rows, count, isPremium);
  if (consecutiveSeats) {
    return consecutiveSeats;
  }

  const fallbackResult: UnitInformation[] = Array.from(rows.values())
    .flatMap(unitsInRow => unitsInRow)
    .slice(0, count);

  if (fallbackResult.length === count) {
    return fallbackResult;
  }

  return [];
};

const getLetterIndex = (letter: string, isPremium: boolean = false): number => {
  const seatsOrder = isPremium ? ["A", "C", "D", "E", "F", "G", "J"] : ["A", "B", "C", "D", "E", "F", "G", "H", "J"];
  return seatsOrder.indexOf(letter);
}

const findFirstXConsecutiveSeats = (rows: Map<string, UnitInformation[]>, count: number, isPremium: boolean): UnitInformation[] | undefined => {
  const firstConsecutiveSequence = Array.from(rows.values())
    .flatMap(units =>
      units.sort((a, b) => getLetterIndex(a.designator.slice(-1), isPremium) - getLetterIndex(b.designator.slice(-1), isPremium))
    )
    .reduce<UnitInformation[] | null>((result, _, i, sortedUnits) => {
      if (result) return result; // Exit early if already found

      const currentSequence = sortedUnits.slice(i, i + count);
      const isConsecutive = currentSequence.length === count && currentSequence.every((unit, index, arr) =>
        index === 0 ||
        getLetterIndex(unit.designator.slice(-1), isPremium) === getLetterIndex((arr[index - 1] as UnitInformation).designator.slice(-1), isPremium) + 1
      );

      return isConsecutive ? currentSequence : null;
    }, null);

  if (firstConsecutiveSequence) {
    return firstConsecutiveSequence;
  }

  return;
}

export const isFlextra = (fare: N0AvailableJourneyFare | undefined): boolean => {
  return fare?.details.productClass === String(CabinProductClass.PREMIUM_PLUS) || fare?.details.productClass === String(CabinProductClass.ECONOMY_PLUS);
}

export const shouldOfferPremium = (seatMap: SeatMapAvailabiliyWithRows, availability: N0SsrByJourney[], journeyKey: string, paxOneKey: string): boolean => {
  return (seatMap.sitTogetherPremiumCabin.sitTwo && isSsrAvailable(seatMap.sitTwoSsr, availability, journeyKey, paxOneKey)) || 
    (seatMap.sitTogetherPremiumCabin.sitThree && isSsrAvailable(seatMap.sitThreeSsr, availability, journeyKey, paxOneKey));
}

export const shouldOfferEconomy = (seatMap: SeatMapAvailabiliyWithRows, availability: N0SsrByJourney[], journeyKey: string, paxOneKey: string): boolean => {
  return (seatMap.sitTogetherEconomyCabin.sitTwo && isSsrAvailable(seatMap.sitTwoSsr, availability, journeyKey, paxOneKey)) || 
    (seatMap.sitTogetherEconomyCabin.sitThree && isSsrAvailable(seatMap.sitThreeSsr, availability, journeyKey, paxOneKey));
}

export const shouldOfferTwo = (seatMap: SeatMapAvailabiliyWithRows, availability: N0SsrByJourney[], journeyKey: string, paxOneKey: string): boolean => {
  return (seatMap.sitTogetherEconomyCabin.sitTwo || seatMap.sitTogetherPremiumCabin.sitTwo) && isSsrAvailable(seatMap.sitTwoSsr, availability, journeyKey, paxOneKey) && getPrice(seatMap.sitTwoSsr, availability, journeyKey, paxOneKey) > 0; 
}

export const shouldOfferThree = (seatMap: SeatMapAvailabiliyWithRows, availability: N0SsrByJourney[], journeyKey: string, paxOneKey: string): boolean => {
  return (seatMap.sitTogetherEconomyCabin.sitThree || seatMap.sitTogetherPremiumCabin.sitThree) && isSsrAvailable(seatMap.sitThreeSsr, availability, journeyKey, paxOneKey) && getPrice(seatMap.sitThreeSsr, availability, journeyKey, paxOneKey) > 0; 
}

const isSsrAvailable = (ssr: string, availability: N0SsrByJourney[], journeyKey: string, paxOneKey: string): boolean => {
  const ssrAvail = availability.find((a) => a.journeyKey === journeyKey)?.seatsSsrs.find((s) => s.ssrCode === ssr);
  const availForPaxOne = !!ssrAvail?.passengersAvailability.some((p) => p.passengerKey === paxOneKey);
  return (ssrAvail?.limitPerPassenger ?? 0)  > 0 && availForPaxOne;
}

export const getPrice = (ssr: string, availability: N0SsrByJourney[], journeyKey: string, paxOneKey: string): number => {
  const ssrAvail = availability.find((a) => a.journeyKey === journeyKey)?.seatsSsrs.find((s) => s.ssrCode === ssr);
  const availForPaxOne = !!ssrAvail?.passengersAvailability.some((p) => p.passengerKey === paxOneKey);
  if (availForPaxOne) {
    return ssrAvail?.passengersAvailability.find((p) => p.passengerKey === paxOneKey)?.price ?? 0;
  }
  return 0;
}
